import Quill, { Delta } from 'quill';
import { Scope } from 'parchment';

type EnterRange = { index: number; length: number };
type EnterContext = { format: Record<string, unknown> };

type KeyboardModule = {
  handleEnter: (range: EnterRange, context: EnterContext) => void;
  bindings: Record<
    string,
    Array<{
      key: string;
      handler: (
        this: { quill: Quill },
        range: EnterRange,
        context: EnterContext,
      ) => unknown;
    }>
  >;
};

/** Quill 2 기본 Enter는 블록 서식만 유지 — 폰트·굵게 등 인라인 서식도 다음 줄에 이어지게 한다 */
const preserveFormatsOnEnter = function (
  this: { quill: Quill },
  range: EnterRange,
  context: EnterContext,
) {
  const lineFormats = Object.keys(context.format).reduce(
    (formats: Record<string, unknown>, format) => {
      if (
        this.quill.scroll.query(format, Scope.BLOCK) &&
        !Array.isArray(context.format[format])
      ) {
        formats[format] = context.format[format];
      }
      return formats;
    },
    {},
  );

  const delta = new Delta()
    .retain(range.index)
    .delete(range.length)
    .insert('\n', lineFormats);
  this.quill.updateContents(delta, Quill.sources.USER);
  this.quill.setSelection(range.index + 1, Quill.sources.SILENT);

  Object.keys(context.format).forEach((name) => {
    if (lineFormats[name] != null) return;
    if (Array.isArray(context.format[name])) return;
    if (name === 'code' || name === 'link') return;
    this.quill.format(
      name,
      context.format[name] as never,
      Quill.sources.USER,
    );
  });

  this.quill.focus();
};

export const installPreserveFormatsOnEnter = (quill: Quill) => {
  const keyboard = quill.getModule('keyboard') as KeyboardModule | undefined;
  if (!keyboard) return;

  const enterBindings = keyboard.bindings.Enter ?? [];
  const defaultHandler = keyboard.handleEnter;

  for (const binding of enterBindings) {
    if (binding.handler === defaultHandler) {
      binding.handler = preserveFormatsOnEnter;
      return;
    }
  }
};
